import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import type { ButtonColors, ButtonProps, ButtonSizes, ButtonVariants } from './types';

export const getBorderColor = cva('', {
  variants: {
    variant: {
      outline: 'border',
      solid: 'border-none',
      soft: 'border-none',
      ghost: 'border-none',
    } satisfies Record<ButtonVariants, string>,
    disabled: {
      true: '',
      false: '',
    },
    color: {
      brand: '',
      neutral: '',
      danger: '',
    } satisfies Record<ButtonColors, string>,
  },
  compoundVariants: [
    { disabled: true, className: 'border-disabled' },
    { variant: 'outline', disabled: false, color: 'brand', className: 'border-brand-secondary' },
    { variant: 'outline', disabled: false, color: 'neutral', className: 'border-gray-300' },
    { variant: 'outline', disabled: false, color: 'danger', className: 'border-utility-error-400' },
  ],
});

export const getBgColor = cva('', {
  variants: {
    variant: {
      solid: '',
      outline: '',
      soft: '',
      ghost: '',
    } satisfies Record<ButtonVariants, string>,
    color: {
      brand: '',
      neutral: '',
      danger: '',
    } satisfies Record<ButtonColors, string>,
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { variant: 'solid', disabled: true, className: 'bg-quaternary' },
    { disabled: true, className: '' },
    { variant: 'solid', disabled: false, color: 'brand', className: 'bg-brand-solid' },
    { variant: 'solid', disabled: false, color: 'neutral', className: 'bg-black' },
    { variant: 'solid', disabled: false, color: 'danger', className: 'bg-error-solid' },
    { variant: 'outline', disabled: false, color: 'brand', className: 'bg-brand-secondary' },
  ],
  defaultVariants: {
    disabled: false,
  },
});

export const getTextColor = cva('', {
  variants: {
    variant: {
      solid: 'text-white',
      outline: '',
      soft: '',
      ghost: '',
    } satisfies Record<ButtonVariants, string>,
    color: {
      brand: '',
      danger: '',
      neutral: '',
    } satisfies Record<ButtonColors, string>,
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { variant: ['outline', 'soft', 'ghost'], color: 'brand', className: 'text-brand-tertiary' },
    { variant: ['outline', 'soft', 'ghost'], color: 'danger', className: 'text-alpha-error-100' },
    { variant: ['outline', 'soft', 'ghost'], color: 'neutral', className: 'text-alpha-black-80' },
    { disabled: true, className: '!text-disabled' },
  ],
});

export const iconSizeVariants = cva('shrink-0', {
  variants: {
    size: {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-4',
      lg: 'size-5',
    } satisfies Record<ButtonSizes, string>,
  },
});

export const getSize = cva('', {
  variants: {
    variant: {
      solid: '',
      outline: '',
      soft: '',
      ghost: 'p-none',
    } satisfies Record<ButtonVariants, string>,
    size: {
      xs: 'p-xs',
      sm: 'p-sm',
      md: 'p-md',
      lg: 'p-lg',
    } satisfies Record<ButtonSizes, string>,
  },
});

export const getTextSize = cva('font-semibold', {
  variants: {
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-sm',
      lg: 'text-md',
    } satisfies Record<ButtonSizes, string>,
  },
});

export const BUTTON_ID_CLASSNAME = 'ua-button';
export const defaultButtonClasses = (
  color: ButtonProps['color'],
  variant: ButtonProps['variant'],
  size: ButtonProps['size'],
  className: string | undefined,
  disabled?: boolean,
) =>
  clsx(
    BUTTON_ID_CLASSNAME,
    getSize({ variant, size }),
    getBgColor({ variant, color, disabled }),
    getBorderColor({ variant, color, disabled }),
    'rounded-md',
    className,
  );
